import { AdminGame, RatingCounts } from '@/lib-backend';

/**
 * Resolve the fixed tag pool used when generating fake reviews for a game.
 * Tags are not operator-selectable: they are "General" plus the game
 * template's own tags. Comments for generation are drawn from this pool.
 */
export function resolveGameAutoTags(game: AdminGame | null): string[] {
    const templateTags = game?.Template.Template.Tags ?? [];
    const all = ['General', ...templateTags]
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
    return Array.from(new Set(all));
}

/**
 * Distribute `count` votes across ratings 1..5 such that the resulting
 * average equals `target` exactly, while `concentration` controls how
 * concentrated vs. spread the result is.
 *
 * Algorithm:
 *   1. Seed with the **minimum-spread** distribution that hits target
 *      exactly: only floor(target) and ceil(target) buckets are used,
 *      split as x = (target − floor) · count at ceil and (count − x) at
 *      floor. For integer targets, all votes go into that single bucket.
 *      With this seed the average matches target by construction.
 *   2. Sum-preserving diffusion passes spread mass outward:
 *      take 2 votes from an inner bucket M ∈ {R=2..4} and put 1 into M-1
 *      and 1 into M+1. Since −2·M + (M-1) + (M+1) = 0, the sum (and the
 *      average) are preserved exactly.
 *
 * `concentration`:
 *   1 — no diffusion: only floor(target) and ceil(target) populated.
 *   0 — many diffusion passes: mass spreads across all five buckets.
 *   In-between caps the number of diffusion passes linearly.
 */
export function buildBellDistribution(
    target: number,
    count: number,
    concentration: number = 1,
): RatingCounts {
    const empty: RatingCounts = {
        Rating1: 0,
        Rating2: 0,
        Rating3: 0,
        Rating4: 0,
        Rating5: 0,
    };
    if (count <= 0 || !Number.isFinite(target)) return empty;
    const t = Math.min(5, Math.max(1, target));
    const c = Math.min(1, Math.max(0, concentration));

    // Phase 1: minimum-spread seed at target.
    const buckets = [0, 0, 0, 0, 0];
    const floor = Math.floor(t);
    const ceil = Math.ceil(t);
    if (floor === ceil) {
        buckets[floor - 1] = count;
    } else {
        let x = Math.round((t - floor) * count); // votes at ceil
        x = Math.max(0, Math.min(count, x));
        buckets[ceil - 1] = x;
        buckets[floor - 1] = count - x;
    }

    // Phase 2: sum-preserving diffusion to add spread.
    const diffuseCap = Math.round((1 - c) * count * 5);
    for (let i = 0; i < diffuseCap; i++) {
        let pickI = -1;
        let pickCount = 1;
        for (let j = 1; j < 4; j++) {
            if (buckets[j] >= 2 && buckets[j] > pickCount) {
                pickCount = buckets[j];
                pickI = j;
            }
        }
        if (pickI === -1) break;
        buckets[pickI] -= 2;
        buckets[pickI - 1] += 1;
        buckets[pickI + 1] += 1;
    }

    return {
        Rating1: buckets[0],
        Rating2: buckets[1],
        Rating3: buckets[2],
        Rating4: buckets[3],
        Rating5: buckets[4],
    };
}

/** Sum of all rating buckets. */
export function totalRatingCount(d: RatingCounts): number {
    return d.Rating1 + d.Rating2 + d.Rating3 + d.Rating4 + d.Rating5;
}

/** Weighted average rating of a distribution; 0 when empty. */
export function averageRating(d: RatingCounts): number {
    const total = totalRatingCount(d);
    if (total === 0) return 0;
    const sum =
        1 * d.Rating1 +
        2 * d.Rating2 +
        3 * d.Rating3 +
        4 * d.Rating4 +
        5 * d.Rating5;
    return sum / total;
}
